import React from 'react';
import { Button } from './ui/button';
import { Play, CheckCircle, ArrowRight } from 'lucide-react';

export const HeroSection = () => {
  return (
    <section id="home" className="min-h-screen pt-16 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center py-16">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-[#6366F1]/10 text-[#6366F1] px-4 py-2 rounded-full text-sm font-medium">
                <CheckCircle className="h-4 w-4" />
                <span>Trusted by 500+ PG Owners</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                <span className="inline-block animate-fadeInUp">RentMatez</span>
                <span className="block text-[#6366F1] animate-bounce-slow"> - Sabse Eazy!</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                Transform your PG business with our comprehensive management platform. 
                Automate rent collection, manage tenants, and streamline operations effortlessly.
              </p>
            </div>

            {/* Key Benefits */}
            <div className="grid sm:grid-cols-2 gap-4 py-6">
              {[
                'Automated Rent Collection',
                'Digital Tenant Management',
                'Maintenance Tracking',
                '24/7 Support'
              ].map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-[#6366F1] hover:bg-[#5856eb] text-white px-8 py-4 text-lg font-semibold group"
              >
                Start 14-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg" 
                className="border-2 border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1] hover:text-white px-8 py-4 text-lg font-semibold group"
              >
                <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Watch Demo
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Trusted by PG owners across India • Available on Android</p>
              <div className="flex items-center space-x-8 text-gray-400">
                <div className="text-2xl font-bold">500+</div>
                <div className="text-2xl font-bold">10K+</div>
                <div className="text-2xl font-bold">25K+</div>
                <div className="text-2xl font-bold">98%</div>
              </div>
            </div>
          </div>

          {/* Right Content - Illustration */}
          <div className="lg:pl-8">
            <div className="relative">
              {/* Background Elements */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1]/20 to-purple-500/20 rounded-3xl transform rotate-6"></div>
              <div className="absolute inset-0 bg-gradient-to-tl from-blue-500/20 to-[#6366F1]/20 rounded-3xl transform -rotate-6"></div>
              
              {/* Main Content */}
              <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                {/* Dashboard Preview */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">PG Dashboard</h3>
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                  
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Total Rooms', value: '45', color: 'bg-blue-500' },
                      { label: 'Occupied', value: '42', color: 'bg-green-500' },
                      { label: 'Pending Rent', value: '₹15K', color: 'bg-yellow-500' },
                      { label: 'This Month', value: '₹1.2L', color: 'bg-[#6366F1]' }
                    ].map((stat, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className={`w-3 h-3 ${stat.color} rounded-full mb-2`}></div>
                        <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                        <div className="text-sm text-gray-600">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Activity List */}
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-700">Recent Activity</div>
                    {[
                      'Rent collected from Room 12',
                      'New tenant registered',
                      'Maintenance completed'
                    ].map((activity, index) => (
                      <div key={index} className="flex items-center space-x-3 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-[#6366F1] rounded-full"></div>
                        <span>{activity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-full p-3 shadow-lg border border-gray-100">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-full p-3 shadow-lg border border-gray-100">
                <div className="w-6 h-6 bg-[#6366F1] rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};